<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import SaSpinner from '@/components/dashboard/ui/SaSpinner.vue'
import AuthSupportLinks from '@/components/views/manage-account/AuthSupportLinks.vue'
import swiftyCheck from '@/assets/images/swifty/swifty-check.svg'

const VerificationStatus = {
  Verifying: 'verifying',
  Success: 'success',
  Failure: 'failure',
}

const store = useStore()
const route = useRoute()
const router = useRouter()

const status = ref(VerificationStatus.Verifying)
const errorMessage = ref('')

const verifying = computed(() => status.value === VerificationStatus.Verifying)
const success = computed(() => status.value === VerificationStatus.Success)
const failure = computed(() => status.value === VerificationStatus.Failure)

const pageTitle = computed(() => {
  if (success.value) return 'AccountVerify.HeaderSuccess'
  if (failure.value) return 'AccountVerify.HeaderFailure'
  return 'AccountVerify.HeaderVerifying'
})

const verifyAccount = async () => {
  if (route.query.confirmation_code === undefined) {
    status.value = VerificationStatus.Failure
    errorMessage.value = 'AccountVerify.ErrorNotFound'
    return
  }

  try {
    await store.dispatch(
      'postAccountVerificationConfirmationCode',
      route.query.confirmation_code
    )

    status.value = VerificationStatus.Success
  } catch (error: any) {
    if (error.response && error.response.status) {
      switch (error.response.status) {
        case 400:
          errorMessage.value = 'AccountVerify.ErrorBadRequest'
          break
        case 401:
          errorMessage.value = 'AccountVerify.ErrorUnauthorized'
          break
        case 404:
          errorMessage.value = 'AccountVerify.ErrorNotFound'
          break
        case 409:
          errorMessage.value = 'AccountVerify.ErrorConflict'
          break
        default:
          errorMessage.value = 'AccountVerify.ErrorServer'
          break
      }
    } else {
      errorMessage.value = 'AccountVerify.ErrorServer'
    }

    status.value = VerificationStatus.Failure
  }
}

const goToAccount = () => {
  router.push({ name: 'Account' })
}

onMounted(() => {
  verifyAccount()
})
</script>

<template>
  <div class="sa-auth-step sa-auth-status-page" data-testid="account-verify">
    <header class="sa-auth-step__header">
      <img
        :src="swiftyCheck"
        class="sa-auth-status-page__swifty"
        alt=""
        width="120"
        height="120"
      />
      <h1 class="sa-auth-step__title">{{ $t(pageTitle) }}</h1>
    </header>

    <div
      v-if="verifying"
      class="sa-auth-status-page__status"
      role="status"
      aria-live="polite"
      :aria-label="$t('AccountVerify.Verifying')"
    >
      <SaSpinner size="page" />
      <p class="sa-auth-step__lead">{{ $t('AccountVerify.Verifying') }}</p>
    </div>

    <div
      v-else-if="success"
      class="sa-auth-status-page__status"
      role="status"
      aria-live="polite"
    >
      <p class="sa-auth-step__lead">{{ $t('AccountVerify.Success') }}</p>
      <button
        type="button"
        class="sa-btn-primary sa-auth-form__submit"
        @click="goToAccount"
      >
        {{ $t('AccountVerify.SuccessCta') }}
      </button>
    </div>

    <div v-else-if="failure" class="sa-auth-status-page__status" role="alert">
      <p class="sa-auth-step__lead">{{ $t(errorMessage) }}</p>
    </div>

    <AuthSupportLinks lead-key="AccountVerify.Support" />
  </div>
</template>
